/**
 * Compiled regex lists with literal prefilters and safe scanning (ENGINE-SPEC §5, §11.6).
 *
 * A `RegexList` is run once per prose block, never over the whole text, so offsets stay
 * block-local (the caller passes `offsetBase`). A regex is executed on a block only when the
 * block contains one of its declared literal `prefilter` substrings; every literal must be
 * required by every match (T-LEX-05 proves soundness). Regexes without a prefilter always run.
 */

import { foldAsciiString } from './chars.ts';

export interface RegexSpec {
	source: string;
	flags: string;
	/** Literal substrings; a match must contain at least one. Compared exactly, or by ASCII fold
	 * when the regex has the `i` flag. Empty or absent means "always scan". */
	prefilter?: readonly string[] | undefined;
}

export type RegexEmit = (
	start: number,
	end: number,
	regexIndex: number,
	m: RegExpExecArray
) => void;

/**
 * Run one regex over a block, emitting every match with block offsets shifted by `offsetBase`.
 * Resets `lastIndex`, steps over empty matches, honours a match cap, and handles non-global
 * regexes (one `exec`). Returns the number of matches emitted.
 */
export function safeScan(
	re: RegExp,
	text: string,
	offsetBase: number,
	emit: (start: number, end: number, m: RegExpExecArray) => void,
	maxMatches = Number.POSITIVE_INFINITY
): number {
	const global = re.global || re.sticky;
	re.lastIndex = 0;
	let n = 0;
	while (n < maxMatches) {
		const m = re.exec(text);
		if (m === null) break;
		n++;
		emit(offsetBase + m.index, offsetBase + m.index + m[0].length, m);
		if (!global) break;
		if (m[0].length === 0) re.lastIndex++;
	}
	re.lastIndex = 0;
	return n;
}

interface Entry {
	re: RegExp;
	fold: boolean;
	literals: readonly string[];
}

export class RegexList {
	readonly length: number;
	readonly regexes: readonly RegExp[];
	private readonly entries: readonly Entry[];

	constructor(specs: readonly RegexSpec[]) {
		this.entries = specs.map((s) => {
			const fold = s.flags.includes('i');
			const literals = (s.prefilter ?? []).map((l) => (fold ? foldAsciiString(l) : l));
			return { re: new RegExp(s.source, s.flags), fold, literals };
		});
		this.regexes = this.entries.map((e) => e.re);
		this.length = specs.length;
	}

	/** True when regex `i` may match in this block (its prefilter passes). */
	admits(i: number, block: string, folded?: string): boolean {
		const e = this.entries[i] as Entry;
		if (e.literals.length === 0) return true;
		const hay = e.fold ? (folded ?? foldAsciiString(block)) : block;
		for (const l of e.literals) if (hay.includes(l)) return true;
		return false;
	}

	/** Scan one block with every admitted regex. Returns the total number of matches. */
	scanBlock(block: string, offsetBase: number, emit: RegexEmit, prefilter = true): number {
		let folded: string | undefined;
		let total = 0;
		for (let i = 0; i < this.entries.length; i++) {
			const e = this.entries[i] as Entry;
			if (prefilter && e.literals.length > 0) {
				if (e.fold && folded === undefined) folded = foldAsciiString(block);
				if (!this.admits(i, block, folded)) continue;
			}
			total += safeScan(e.re, block, offsetBase, (s, en, m) => emit(s, en, i, m));
		}
		return total;
	}

	/** Matches per regex in one block. */
	countEach(block: string, prefilter = true): Uint32Array {
		const out = new Uint32Array(this.length);
		this.scanBlock(
			block,
			0,
			(_s, _e, i) => {
				out[i] = (out[i] as number) + 1;
			},
			prefilter
		);
		return out;
	}
}
